import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Responsive, WidthProvider } from 'react-grid-layout/legacy';
import {
  Card,
  Title,
  Metric,
  Text,
  Table,
  TableHead,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  Button,
} from '@tremor/react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  Legend,
} from 'recharts';
import 'react-grid-layout/css/styles.css';
import kpiAPI from '../../api/kpi';
import { computePowyzej24hKltGlt } from '../../utils/kpiPowyzej24h';
import DatePicker from './DatePicker';
import './KPIWidget.css';

const ResponsiveGridLayout = WidthProvider(Responsive);

const PROJECT = 'MAN';
const STORAGE_LAYOUT = 'kpi-widget-layout';
const STORAGE_WIDGETS = 'kpi-widget-widgets';

const CHART_COLORS = ['#00bcd4', '#ff9800', '#66bb6a', '#7986cb', '#e65100', '#9c27b0', '#26a69a', '#42a5f5'];

function normalizeSheetData(data) {
  if (!data || !Array.isArray(data)) return [];
  return data.map((row) => {
    if (!Array.isArray(row)) return [{ value: '' }];
    return row.map((cell) => {
      if (cell && typeof cell === 'object' && 'value' in cell) return cell;
      return { value: cell != null ? String(cell) : '' };
    });
  });
}

function getCell(row, col) {
  if (col < 0 || !row || !row[col]) return '';
  const cell = row[col];
  if (cell && typeof cell === 'object' && 'value' in cell) return cell.value != null ? String(cell.value).trim() : '';
  return cell != null ? String(cell).trim() : '';
}

function getColumnIndex(headers, name) {
  const needle = String(name || '').trim().toLowerCase();
  return headers.findIndex((h) => String(h || '').trim().toLowerCase() === needle) ?? -1;
}

function parseDateOnly(val) {
  if (val == null || val === '') return null;
  const s = String(val).trim();
  if (!s) return null;
  const datePart = s.includes(' ') ? s.split(/\s/)[0] : s;
  const iso = /^\d{4}-\d{2}-\d{2}/.exec(datePart);
  if (iso) return new Date(iso[0]);
  const ddmmyy = datePart.match(/^(\d{1,2})[./](\d{1,2})[./](\d{4})$/);
  if (ddmmyy) return new Date(parseInt(ddmmyy[3], 10), parseInt(ddmmyy[2], 10) - 1, parseInt(ddmmyy[1], 10));
  return new Date(datePart);
}

function parseNumber(val) {
  if (val == null || val === '') return 0;
  const v = typeof val === 'object' && val !== null && 'value' in val ? val.value : val;
  const n = Number(String(v).replace(/\s/g, '').replace(',', '.'));
  return Number.isNaN(n) ? 0 : n;
}

function dateToStr(d) {
  if (!d || !d.getTime) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatDateDisplay(d) {
  if (!d) return '—';
  const date = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(date.getTime())) return '—';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}.${month}.${year}`;
}

function aggregateRaportByDay(rows, headers) {
  const dataCol = getColumnIndex(headers, 'DATA');
  const zmianaCol = getColumnIndex(headers, 'ZMIANA');
  if (dataCol < 0) return {};
  const bhpCol = getColumnIndex(headers, 'BHP');
  const pickCol = getColumnIndex(headers, 'ilość pick');
  const shipSqCol = getColumnIndex(headers, 'SHIP SQ');
  const shipRepCol = getColumnIndex(headers, 'SHIP REP');
  const shipLergutCol = getColumnIndex(headers, 'SHIP LERGUT');
  const repPickCol = getColumnIndex(headers, 'REP PICK');
  const inboundCol = getColumnIndex(headers, 'INBOUND');
  const byDay = {};
  rows.forEach((row) => {
    const val = row[dataCol] && typeof row[dataCol] === 'object' ? row[dataCol].value : row[dataCol];
    const d = parseDateOnly(val);
    if (!d) return;
    const dateStr = dateToStr(d);
    if (!byDay[dateStr]) {
      byDay[dateStr] = {
        bhp: 0, bhpHasEvent: false, pick: 0, pickA: 0, pickB: 0,
        shipSq: 0, shipRep: 0, shipLergut: 0,
        shipSqA: 0, shipSqB: 0, shipRepA: 0, shipRepB: 0, shipLergutA: 0, shipLergutB: 0,
        repPick: 0, repPickA: 0, repPickB: 0,
        inbound: 0, inboundA: 0, inboundB: 0,
      };
    }
    const z = zmianaCol >= 0 ? String((row[zmianaCol] && typeof row[zmianaCol] === 'object' ? row[zmianaCol].value : row[zmianaCol]) ?? '').trim().toUpperCase() : '';
    const isB = z === 'B';
    if (pickCol >= 0) {
      const v = parseNumber(row[pickCol]);
      byDay[dateStr].pick += v;
      if (isB) byDay[dateStr].pickB += v; else byDay[dateStr].pickA += v;
    }
    const sq = shipSqCol >= 0 ? parseNumber(row[shipSqCol]) : 0;
    const rep = shipRepCol >= 0 ? parseNumber(row[shipRepCol]) : 0;
    const lerg = shipLergutCol >= 0 ? parseNumber(row[shipLergutCol]) : 0;
    byDay[dateStr].shipSq += sq;
    byDay[dateStr].shipRep += rep;
    byDay[dateStr].shipLergut += lerg;
    if (isB) {
      byDay[dateStr].shipSqB += sq;
      byDay[dateStr].shipRepB += rep;
      byDay[dateStr].shipLergutB += lerg;
    } else {
      byDay[dateStr].shipSqA += sq;
      byDay[dateStr].shipRepA += rep;
      byDay[dateStr].shipLergutA += lerg;
    }
    if (repPickCol >= 0) {
      const rv = parseNumber(row[repPickCol]);
      byDay[dateStr].repPick += rv;
      if (isB) byDay[dateStr].repPickB += rv; else byDay[dateStr].repPickA += rv;
    }
    if (inboundCol >= 0) {
      const inv = parseNumber(row[inboundCol]);
      byDay[dateStr].inbound += inv;
      if (isB) byDay[dateStr].inboundB += inv; else byDay[dateStr].inboundA += inv;
    }
  });
  return byDay;
}

function getDateColumnIndex(headers) {
  const names = (headers || []).map((h) => String(h || '').trim().toUpperCase());
  const idx = names.findIndex((n) => n === 'DATA WPISU' || n.includes('DATA WPISU') || n === 'DATA' || n.startsWith('DATA'));
  return idx >= 0 ? idx : 0;
}

function aggregatePsByDay(rows, headers) {
  const dateCol = getDateColumnIndex(headers);
  const iloscCol = getColumnIndex(headers, 'ILOŚĆ');
  if (iloscCol < 0) return {};
  const byDay = {};
  rows.forEach((row) => {
    const val = row[dateCol] && typeof row[dateCol] === 'object' ? row[dateCol].value : row[dateCol];
    const d = parseDateOnly(val);
    if (!d) return;
    const dateStr = dateToStr(d);
    byDay[dateStr] = (byDay[dateStr] || 0) + parseNumber(row[iloscCol]);
  });
  return byDay;
}

function filterPsRowsByDate(rows, headers, dateStr) {
  if (!rows?.length || !dateStr) return [];
  const dateCol = getDateColumnIndex(headers);
  const [y, m, day] = dateStr.split('-').map(Number);
  return rows.filter((row) => {
    const val = row[dateCol] && typeof row[dateCol] === 'object' ? row[dateCol].value : row[dateCol];
    const d = parseDateOnly(val);
    if (!d) return false;
    return d.getFullYear() === y && d.getMonth() === m - 1 && d.getDate() === day;
  });
}

function getTop3PsaFromRows(rows, headers) {
  const psaCol = getColumnIndex(headers, 'PSA');
  const iloscCol = getColumnIndex(headers, 'ILOŚĆ');
  if (psaCol < 0 || iloscCol < 0) return [];
  const byPsa = new Map();
  rows.forEach((row) => {
    const psa = String((row[psaCol] && typeof row[psaCol] === 'object' ? row[psaCol].value : row[psaCol]) ?? '').trim() || '(pusty)';
    byPsa.set(psa, (byPsa.get(psa) || 0) + parseNumber(row[iloscCol]));
  });
  return Array.from(byPsa.entries())
    .map(([PSA, ILOŚĆ]) => ({ PSA, ILOŚĆ }))
    .sort((a, b) => b.ILOŚĆ - a.ILOŚĆ)
    .slice(0, 10);
}

function getTrendPickOww14(byDayRaport, displayDateStr) {
  const dates = Object.keys(byDayRaport || {}).filter((d) => d <= (displayDateStr || '')).sort();
  const slice = dates.slice(-14);
  return slice.map((d) => {
    const day = byDayRaport[d];
    return {
      dateStr: d,
      label: d.slice(8) + '.' + d.slice(5, 7),
      pick: (day && day.pick) || 0,
      oww: (day && day.repPick) || 0,
    };
  });
}

function getTransportTrend14(byDayRaport, displayDateStr) {
  const dates = Object.keys(byDayRaport || {}).filter((d) => d <= (displayDateStr || '')).sort();
  const slice = dates.slice(-14);
  return slice.map((d) => {
    const day = byDayRaport[d];
    const sq = (day && day.shipSq) ?? 0;
    const rep = (day && day.shipRep) ?? 0;
    const lergut = (day && day.shipLergut) ?? 0;
    return {
      dateStr: d,
      label: d.slice(8) + '.' + d.slice(5, 7),
      sq,
      rep,
      lergut,
      razem: sq + rep + lergut,
    };
  });
}

function getInboundTrend14(byDayRaport, displayDateStr) {
  const dates = Object.keys(byDayRaport || {}).filter((d) => d <= (displayDateStr || '')).sort();
  const slice = dates.slice(-14);
  return slice.map((d) => {
    const day = byDayRaport[d];
    return {
      dateStr: d,
      label: d.slice(8) + '.' + d.slice(5, 7),
      inbound: (day && day.inbound) ?? 0,
    };
  });
}

const WIDGET_TYPES = [
  { id: 'metric', label: 'Kafelek / metryka', icon: '📊' },
  { id: 'line', label: 'Wykres liniowy', icon: '📈' },
  { id: 'bar', label: 'Wykres słupkowy', icon: '📊' },
  { id: 'pie', label: 'Wykres kołowy', icon: '🥧' },
  { id: 'donut', label: 'Wykres donut', icon: '🍩' },
  { id: 'area', label: 'Wykres obszarowy', icon: '📉' },
  { id: 'table', label: 'Tabela', icon: '📋' },
  { id: 'combo', label: 'Kombinacja (kafelek + wykres)', icon: '🔗' },
];

const METRIC_KEYS = [
  { id: 'bhp', label: 'BHP' },
  { id: 'pick', label: 'PICK' },
  { id: 'repPick', label: 'REPLENISHMENT' },
  { id: 'psSum', label: 'PS' },
  { id: 'inbound', label: 'ZMAGAZYNOWANE' },
  { id: 'transport', label: 'Transport' },
  { id: 'powyzej24hRazem', label: 'Brak przyjęcia >24h' },
  { id: 'najstarszaNieprzyjetaDni', label: 'Najstarszy nieprzyjęty (dni)' },
];

const CHART_KEYS = [
  { id: 'pick', label: 'Trend PICK' },
  { id: 'transport', label: 'Trend Transport (SQ/REP/LERGUT)' },
  { id: 'inbound', label: 'Trend Zmagazynowane' },
  { id: 'oww', label: 'Trend Replenishment' },
];

const TABLE_KEYS = [
  { id: 'top10', label: 'Top 10 PSA (ilość)' },
  { id: 'ps', label: 'Lista PS / Błędy (pełna)' },
  { id: 'top5oldest', label: 'Top 5 najstarszych (SNR + Magazyn)' },
];

const DEFAULT_LAYOUTS = { lg: [], md: [], sm: [] };
const DEFAULT_WIDGETS = [];

function generateId() {
  return `w_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

function KPIWidget() {
  const [layouts, setLayouts] = useState(DEFAULT_LAYOUTS);
  const [widgets, setWidgets] = useState(DEFAULT_WIDGETS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [displayDate, setDisplayDate] = useState(null);
  const [availableDates, setAvailableDates] = useState([]);
  const [addWidgetOpen, setAddWidgetOpen] = useState(false);
  const [configWidgetId, setConfigWidgetId] = useState(null);
  const [dashboardData, setDashboardData] = useState({
    summary: {},
    trendPick14: [],
    transportTrend14: [],
    inboundTrend14: [],
    psTable: { headers: [], rows: [] },
    top10Psa: [],
    top5Oldest: [],
  });
  const dataRef = useRef({});

  useEffect(() => {
    try {
      const rawLayout = localStorage.getItem(STORAGE_LAYOUT);
      const rawWidgets = localStorage.getItem(STORAGE_WIDGETS);
      if (rawLayout) {
        const parsed = JSON.parse(rawLayout);
        if (parsed && typeof parsed === 'object') setLayouts(parsed);
      }
      if (rawWidgets) {
        const parsed = JSON.parse(rawWidgets);
        if (parsed && Array.isArray(parsed)) setWidgets(parsed);
      }
    } catch (e) {}
  }, []);

  const saveLayout = useCallback((newLayouts) => {
    setLayouts(newLayouts);
    try {
      localStorage.setItem(STORAGE_LAYOUT, JSON.stringify(newLayouts));
    } catch (e) {}
  }, []);

  const saveWidgets = useCallback((newWidgets) => {
    setWidgets(newWidgets);
    try {
      localStorage.setItem(STORAGE_WIDGETS, JSON.stringify(newWidgets));
    } catch (e) {}
  }, []);

  const loadData = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const res = await kpiAPI.getReports(PROJECT);
      const list = Array.isArray(res?.sheets) ? res.sheets : [];
      const raportSheet = list.find((s) => String(s.name || '').trim() === 'Raport');
      const psSheet = list.find((s) => String(s.name || '').trim() === 'PS');
      const powyzejSheet = list.find((s) => String(s.name || '').trim() === 'POWYŻEJ24H');
      const pojemnikiSheet = list.find((s) => String(s.name || '').trim() === 'POJEMNIKI');

      const raportData = raportSheet ? normalizeSheetData(raportSheet.data) : [];
      const psData = psSheet ? normalizeSheetData(psSheet.data) : [];
      const powyzejData = powyzejSheet?.data ?? [];
      const pojemnikiData = pojemnikiSheet?.data ?? [];
      const raportHeaders = raportData[0]?.map((c) => (c && typeof c === 'object' ? c.value : c)) ?? [];
      const psHeaders = psData[0]?.map((c) => (c && typeof c === 'object' ? c.value : c)) ?? [];
      const raportRows = raportData.slice(1);
      const psRows = psData.slice(1);

      const byDayRaport = aggregateRaportByDay(raportRows, raportHeaders);
      const byDayPs = aggregatePsByDay(psRows, psHeaders);
      const raportDates = Object.keys(byDayRaport).sort();
      const psDates = Object.keys(byDayPs).sort();
      const lastRaport = raportDates.length ? raportDates[raportDates.length - 1] : null;
      const lastPs = psDates.length ? psDates[psDates.length - 1] : null;
      const dates = [lastRaport, lastPs].filter(Boolean);
      const displayDateStr = dates.length ? dates.sort()[dates.length - 1] : null;
      const datesList = Array.from(new Set([...raportDates, ...psDates])).sort();
      const powyzej24h = computePowyzej24hKltGlt(powyzejData, pojemnikiData);

      const r = byDayRaport[displayDateStr] || {};
      const hasRaport = Object.keys(byDayRaport).length > 0;
      const transportTotal = (r.shipSqA ?? 0) + (r.shipSqB ?? 0) + (r.shipRepA ?? 0) + (r.shipRepB ?? 0) + (r.shipLergutA ?? 0) + (r.shipLergutB ?? 0);
      const nd = powyzej24h?.najstarszaData ? Math.floor((Date.now() - (powyzej24h.najstarszaData instanceof Date ? powyzej24h.najstarszaData : new Date(powyzej24h.najstarszaData)).getTime()) / 86400000) : null;

      const dayRows = filterPsRowsByDate(psRows, psHeaders, displayDateStr);
      const top10Psa = getTop3PsaFromRows(dayRows, psHeaders);

      const effectiveDate = displayDateStr || datesList[datesList.length - 1];
      setDisplayDate(effectiveDate);
      setAvailableDates(datesList);
      setDashboardData({
        summary: {
          bhp: hasRaport ? (r.bhp ?? null) : null,
          pick: hasRaport ? (r.pick ?? null) : null,
          repPick: hasRaport ? (r.repPick ?? null) : null,
          psSum: Object.keys(byDayPs).length > 0 ? (byDayPs[displayDateStr] ?? null) : null,
          inbound: hasRaport ? (r.inbound ?? null) : null,
          transport: transportTotal,
          powyzej24hRazem: powyzej24h?.razem ?? null,
          najstarszaNieprzyjetaDni: nd,
        },
        trendPick14: getTrendPickOww14(byDayRaport, displayDateStr),
        transportTrend14: getTransportTrend14(byDayRaport, displayDateStr),
        inboundTrend14: getInboundTrend14(byDayRaport, displayDateStr),
        psTable: { headers: psHeaders, rows: dayRows },
        top10Psa,
        top5Oldest: powyzej24h?.top5Oldest || [],
      });
      dataRef.current = { byDayRaport, byDayPs, psRows, psHeaders, powyzej24h };
      dataRef.current._lastDisplayDate = displayDateStr;
    } catch (err) {
      console.error('Błąd ładowania Widget:', err);
      setError('Nie udało się załadować danych.');
    } finally {
      setLoading(false);
    }
  }, []);

  const applyDisplayDate = useCallback((dateStr) => {
    const ref = dataRef.current;
    if (!ref || !ref.byDayRaport) return;
    const { byDayRaport, byDayPs, psRows, psHeaders, powyzej24h } = ref;
    const r = byDayRaport[dateStr] || {};
    const hasRaport = Object.keys(byDayRaport).length > 0;
    const transportTotal = (r.shipSqA ?? 0) + (r.shipSqB ?? 0) + (r.shipRepA ?? 0) + (r.shipRepB ?? 0) + (r.shipLergutA ?? 0) + (r.shipLergutB ?? 0);
    const nd = powyzej24h?.najstarszaData ? Math.floor((Date.now() - (powyzej24h.najstarszaData instanceof Date ? powyzej24h.najstarszaData : new Date(powyzej24h.najstarszaData)).getTime()) / 86400000) : null;
    const dayRows = filterPsRowsByDate(psRows, psHeaders, dateStr);
    const top10Psa = getTop3PsaFromRows(dayRows, psHeaders);
    setDisplayDate(dateStr);
    setDashboardData({
      summary: {
        bhp: hasRaport ? (r.bhp ?? null) : null,
        pick: hasRaport ? (r.pick ?? null) : null,
        repPick: hasRaport ? (r.repPick ?? null) : null,
        psSum: Object.keys(byDayPs).length > 0 ? (byDayPs[dateStr] ?? null) : null,
        inbound: hasRaport ? (r.inbound ?? null) : null,
        transport: transportTotal,
        powyzej24hRazem: powyzej24h?.razem ?? null,
        najstarszaNieprzyjetaDni: nd,
      },
      trendPick14: getTrendPickOww14(byDayRaport, dateStr),
      transportTrend14: getTransportTrend14(byDayRaport, dateStr),
      inboundTrend14: getInboundTrend14(byDayRaport, dateStr),
      psTable: { headers: psHeaders, rows: dayRows },
      top10Psa,
      top5Oldest: powyzej24h?.top5Oldest || [],
    });
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const onLayoutChange = useCallback(
    (layout, allLayouts) => {
      saveLayout(allLayouts);
    },
    [saveLayout]
  );

  const addWidget = (type) => {
    const id = generateId();
    const newWidget = {
      id,
      type,
      title: WIDGET_TYPES.find((t) => t.id === type)?.label || type,
      metricKey: METRIC_KEYS[0]?.id || 'bhp',
      chartKey: CHART_KEYS[0]?.id || 'pick',
      tableKey: TABLE_KEYS[0]?.id || 'top10',
      backgroundColor: '#252d3d',
      borderColor: '#334155',
      textColor: '#f1f5f9',
      chartColors: [...CHART_COLORS],
    };
    const count = widgets.length;
    const newLayout = {
      i: id,
      x: (count * 2) % 12,
      y: Math.floor(count / 6) * 4,
      w: type === 'table' || type === 'combo' ? 4 : 2,
      h: type === 'metric' ? 2 : 4,
    };
    const lg = [...(layouts.lg || []), newLayout];
    const md = [...(layouts.md || []), { ...newLayout, x: (count * 2) % 6 }];
    const sm = [...(layouts.sm || []), { ...newLayout, x: 0, y: count * 4, w: 1 }];
    setLayouts({ lg, md, sm });
    setWidgets([...widgets, newWidget]);
    saveLayout({ lg, md, sm });
    saveWidgets([...widgets, newWidget]);
    setAddWidgetOpen(false);
  };

  const removeWidget = (id) => {
    setWidgets((w) => w.filter((x) => x.id !== id));
    setLayouts((prev) => {
      const filter = (arr) => (arr || []).filter((item) => item.i !== id);
      return { lg: filter(prev.lg), md: filter(prev.md), sm: filter(prev.sm) };
    });
    saveWidgets(widgets.filter((w) => w.id !== id));
    saveLayout(
      Object.fromEntries(
        Object.entries(layouts).map(([k, v]) => [k, (v || []).filter((item) => item.i !== id)])
      )
    );
    setConfigWidgetId(null);
  };

  const updateWidget = (id, patch) => {
    const next = widgets.map((w) => (w.id === id ? { ...w, ...patch } : w));
    setWidgets(next);
    saveWidgets(next);
    if (patch.title !== undefined || patch.backgroundColor !== undefined) setConfigWidgetId(null);
  };

  const fmt = (v) => (v != null ? String(v) : '—');
  const fmtNum = (v) => (v != null ? String(Math.round(v)) : '—');

  const renderWidgetContent = (w) => {
    const summary = dashboardData.summary || {};
    const s = summary;

    if (w.type === 'metric') {
      const key = w.metricKey || 'bhp';
      const val = s[key];
      return (
        <div className="kpi-widget-metric-body">
          <Metric className="kpi-widget-metric-value">{fmtNum(val)}</Metric>
          <Text className="kpi-widget-metric-label">{METRIC_KEYS.find((m) => m.id === key)?.label || key}</Text>
        </div>
      );
    }

    if (w.type === 'line') {
      const key = w.chartKey || 'pick';
      let data = [];
      if (key === 'pick') data = dashboardData.trendPick14.map((d) => ({ ...d, value: d.pick }));
      else if (key === 'oww') data = dashboardData.trendPick14.map((d) => ({ ...d, value: d.oww }));
      else if (key === 'inbound') data = dashboardData.inboundTrend14.map((d) => ({ ...d, value: d.inbound }));
      else if (key === 'transport') data = dashboardData.transportTrend14.map((d) => ({ ...d, value: d.razem }));

      const color = w.chartColors?.[0] || CHART_COLORS[0];
      return (
        <ResponsiveContainer width="100%" height="100%" minHeight={120}>
          <LineChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#334155" strokeOpacity={0.6} />
            <XAxis dataKey="label" tick={{ fill: '#94a3b8', fontSize: 10 }} />
            <YAxis tick={{ fill: '#94a3b8', fontSize: 10 }} allowDecimals={false} />
            <Tooltip contentStyle={{ background: '#1e2433', border: '1px solid #334155', borderRadius: 6 }} />
            <Line type="monotone" dataKey="value" stroke={color} strokeWidth={2} dot={{ fill: color, r: 2 }} />
          </LineChart>
        </ResponsiveContainer>
      );
    }

    if (w.type === 'bar') {
      const data = dashboardData.inboundTrend14 || [];
      const color = w.chartColors?.[0] || CHART_COLORS[5];
      return (
        <ResponsiveContainer width="100%" height="100%" minHeight={120}>
          <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#334155" strokeOpacity={0.6} />
            <XAxis dataKey="label" tick={{ fill: '#94a3b8', fontSize: 10 }} />
            <YAxis tick={{ fill: '#94a3b8', fontSize: 10 }} allowDecimals={false} />
            <Tooltip contentStyle={{ background: '#1e2433', border: '1px solid #334155', borderRadius: 6 }} />
            <Bar dataKey="inbound" fill={color} radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      );
    }

    if (w.type === 'pie' || w.type === 'donut') {
      const data = [
        { name: 'PICK', value: s.pick || 0 },
        { name: 'REPLENISHMENT', value: s.repPick || 0 },
        { name: 'ZMAGAZYNOWANE', value: s.inbound || 0 },
        { name: 'PS', value: s.psSum || 0 },
      ].filter((d) => d.value > 0);
      const colors = w.chartColors || CHART_COLORS;
      return (
        <ResponsiveContainer width="100%" height="100%" minHeight={120}>
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={w.type === 'donut' ? '60%' : 0}
              outerRadius="80%"
              paddingAngle={2}
              dataKey="value"
              label={({ name, value }) => `${name}: ${value}`}
            >
              {data.map((_, i) => (
                <Cell key={i} fill={colors[i % colors.length]} />
              ))}
            </Pie>
            <Tooltip contentStyle={{ background: '#1e2433', border: '1px solid #334155', borderRadius: 6 }} />
          </PieChart>
        </ResponsiveContainer>
      );
    }

    if (w.type === 'area') {
      const data = dashboardData.transportTrend14 || [];
      const colors = w.chartColors || CHART_COLORS;
      return (
        <ResponsiveContainer width="100%" height="100%" minHeight={120}>
          <AreaChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#334155" strokeOpacity={0.6} />
            <XAxis dataKey="label" tick={{ fill: '#94a3b8', fontSize: 10 }} />
            <YAxis tick={{ fill: '#94a3b8', fontSize: 10 }} allowDecimals={false} />
            <Tooltip contentStyle={{ background: '#1e2433', border: '1px solid #334155', borderRadius: 6 }} />
            <Area type="monotone" dataKey="sq" stackId="1" stroke={colors[0]} fill={colors[0]} fillOpacity={0.6} />
            <Area type="monotone" dataKey="rep" stackId="1" stroke={colors[1]} fill={colors[1]} fillOpacity={0.6} />
            <Area type="monotone" dataKey="lergut" stackId="1" stroke={colors[2]} fill={colors[2]} fillOpacity={0.6} />
          </AreaChart>
        </ResponsiveContainer>
      );
    }

    if (w.type === 'table') {
      const tableKey = w.tableKey || 'top10';
      if (tableKey === 'top5oldest') {
        const rows = dashboardData.top5Oldest || [];
        return (
          <div className="kpi-widget-table-wrap">
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>SNR</TableHeaderCell>
                  <TableHeaderCell>Magazyn</TableHeaderCell>
                  <TableHeaderCell>DATA PRZYJĘCIA</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} className="kpi-widget-empty">Brak pozycji starszych niż 24h</TableCell>
                  </TableRow>
                ) : (
                  rows.map((r, i) => (
                    <TableRow key={`${r.snr}-${r.magazyn}-${i}`}>
                      <TableCell>{r.snr}</TableCell>
                      <TableCell>{r.magazyn}</TableCell>
                      <TableCell>{formatDateDisplay(r.date)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        );
      }
      if (tableKey === 'ps') {
        const { headers = [], rows = [] } = dashboardData.psTable || {};
        return (
          <div className="kpi-widget-table-wrap">
            <Table>
              <TableHead>
                <TableRow>
                  {headers.map((h, i) => (
                    <TableHeaderCell key={i}>{h || `Kolumna ${i + 1}`}</TableHeaderCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={Math.max(1, headers.length)} className="kpi-widget-empty">Brak wpisów za wybrany dzień</TableCell>
                  </TableRow>
                ) : (
                  rows.slice(0, 15).map((row, idx) => (
                    <TableRow key={idx}>
                      {row.map((cell, c) => (
                        <TableCell key={c}>{cell && typeof cell === 'object' ? cell.value : cell}</TableCell>
                      ))}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        );
      }
      const rows = dashboardData.top10Psa || [];
      return (
        <div className="kpi-widget-table-wrap">
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>PSA</TableHeaderCell>
                <TableHeaderCell>Ilość</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={2} className="kpi-widget-empty">Brak danych</TableCell>
                </TableRow>
              ) : (
                rows.map((r, i) => (
                  <TableRow key={i}>
                    <TableCell>{r.PSA}</TableCell>
                    <TableCell>{r.ILOŚĆ}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      );
    }

    if (w.type === 'combo') {
      const key = w.metricKey || 'pick';
      const val = s[key];
      const chartKey = w.chartKey || 'pick';
      let lineData = [];
      if (chartKey === 'pick') lineData = dashboardData.trendPick14?.map((d) => ({ ...d, value: d.pick })) || [];
      else if (chartKey === 'oww') lineData = dashboardData.trendPick14?.map((d) => ({ ...d, value: d.oww })) || [];
      else if (chartKey === 'inbound') lineData = dashboardData.inboundTrend14?.map((d) => ({ ...d, value: d.inbound })) || [];
      else if (chartKey === 'transport') lineData = dashboardData.transportTrend14?.map((d) => ({ ...d, value: d.razem })) || [];
      const color = w.chartColors?.[0] || CHART_COLORS[0];
      return (
        <div className="kpi-widget-combo">
          <div className="kpi-widget-combo-metric">
            <Metric>{fmtNum(val)}</Metric>
            <Text>{METRIC_KEYS.find((m) => m.id === key)?.label || key}</Text>
          </div>
          <div className="kpi-widget-combo-chart">
            <ResponsiveContainer width="100%" height={80}>
              <LineChart data={lineData} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
                <Line type="monotone" dataKey="value" stroke={color} strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      );
    }

    return <Text className="kpi-widget-empty">Nieznany typ widgetu</Text>;
  };

  if (loading) {
    return (
      <div className="kpi-widget-page">
        <div className="kpi-widget-header">
          <Title>Widget KPI</Title>
          <Text>Ładowanie…</Text>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="kpi-widget-page">
        <div className="kpi-widget-header">
          <Title>Widget KPI</Title>
          <Text className="kpi-widget-error">{error}</Text>
        </div>
      </div>
    );
  }

  return (
    <div className="kpi-widget-page">
      <div className="kpi-widget-header">
        <div>
          <Title>Widget KPI</Title>
          {displayDate && (
            <div className="kpi-widget-header-date-row">
              <DatePicker
                label="Wybierz dzień"
                value={displayDate || ''}
                min={availableDates[0] || undefined}
                max={availableDates[availableDates.length - 1] || undefined}
                placeholder="Wybierz dzień"
                className="kpi-date-picker-dashboard"
                onChange={(v) => {
                  if (v && dataRef.current?.byDayRaport) applyDisplayDate(v);
                }}
              />
            </div>
          )}
        </div>
        <div className="kpi-widget-toolbar">
          <div className="kpi-widget-add-wrap">
            <Button size="sm" onClick={() => setAddWidgetOpen(!addWidgetOpen)}>
              + Dodaj widget
            </Button>
            {addWidgetOpen && (
              <div className="kpi-widget-add-dropdown">
                {WIDGET_TYPES.map((t) => (
                  <button key={t.id} type="button" className="kpi-widget-add-item" onClick={() => addWidget(t.id)}>
                    <span className="kpi-widget-add-icon">{t.icon}</span>
                    {t.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          <Button size="sm" variant="secondary" onClick={loadData}>
            Odśwież dane
          </Button>
        </div>
      </div>

      {widgets.length > 0 && (
      <ResponsiveGridLayout
        className="kpi-widget-grid"
        layouts={layouts}
        breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
        cols={{ lg: 12, md: 6, sm: 1, xs: 1, xxs: 1 }}
        rowHeight={80}
        onLayoutChange={onLayoutChange}
        isDraggable
        isResizable
        compactType="vertical"
        preventCollision={false}
      >
        {widgets.map((w) => (
          <div key={w.id} data-grid={{ w: 2, h: 4, minW: 1, minH: 2 }}>
            <Card
              className={`kpi-widget-card kpi-widget-card-title-${w.titleFontSize || 'default'}`}
              style={{
                backgroundColor: w.backgroundColor || '#252d3d',
                borderColor: w.borderColor || '#334155',
                color: w.textColor || '#f1f5f9',
              }}
            >
              <div className="kpi-widget-card-header">
                <Title className="kpi-widget-card-title" style={{ color: w.textColor || '#f1f5f9' }}>{w.title}</Title>
                <div className="kpi-widget-card-actions">
                  <button
                    type="button"
                    className="kpi-widget-btn-icon"
                    onClick={() => setConfigWidgetId(configWidgetId === w.id ? null : w.id)}
                    title="Ustawienia"
                  >
                    ⚙
                  </button>
                  <button type="button" className="kpi-widget-btn-icon kpi-widget-btn-remove" onClick={() => removeWidget(w.id)} title="Usuń">
                    ×
                  </button>
                </div>
              </div>
              {configWidgetId === w.id && (
                <div className="kpi-widget-config">
                  <label>
                    Tytuł:
                    <input
                      type="text"
                      value={w.title}
                      onChange={(e) => updateWidget(w.id, { title: e.target.value })}
                      className="kpi-widget-config-input"
                    />
                  </label>
                  <label>
                    Tło:
                    <input
                      type="color"
                      value={w.backgroundColor || '#252d3d'}
                      onChange={(e) => updateWidget(w.id, { backgroundColor: e.target.value })}
                      className="kpi-widget-config-color"
                    />
                  </label>
                  <label>
                    Obramowanie:
                    <input
                      type="color"
                      value={w.borderColor || '#334155'}
                      onChange={(e) => updateWidget(w.id, { borderColor: e.target.value })}
                      className="kpi-widget-config-color"
                    />
                  </label>
                  {(w.type === 'metric' || w.type === 'combo') && (
                    <label>
                      Metryka:
                      <select
                        value={w.metricKey}
                        onChange={(e) => updateWidget(w.id, { metricKey: e.target.value })}
                        className="kpi-widget-config-select"
                      >
                        {METRIC_KEYS.map((m) => (
                          <option key={m.id} value={m.id}>
                            {m.label}
                          </option>
                        ))}
                      </select>
                    </label>
                  )}
                  {['line', 'area', 'combo'].includes(w.type) && (
                    <label>
                      Wykres / dane trendu:
                      <select
                        value={w.chartKey}
                        onChange={(e) => updateWidget(w.id, { chartKey: e.target.value })}
                        className="kpi-widget-config-select"
                      >
                        {CHART_KEYS.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.label}
                          </option>
                        ))}
                      </select>
                    </label>
                  )}
                  {w.type === 'table' && (
                    <label>
                      Źródło danych:
                      <select
                        value={w.tableKey || 'top10'}
                        onChange={(e) => updateWidget(w.id, { tableKey: e.target.value })}
                        className="kpi-widget-config-select"
                      >
                        {TABLE_KEYS.map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.label}
                          </option>
                        ))}
                      </select>
                    </label>
                  )}
                  {['line', 'bar', 'area', 'pie', 'donut', 'combo'].includes(w.type) && (
                    <label>
                      Kolor wykresu:
                      <div className="kpi-widget-config-colors">
                        {(w.chartColors || CHART_COLORS).slice(0, w.type === 'combo' ? 1 : 6).map((c, i) => (
                          <input
                            key={i}
                            type="color"
                            value={c}
                            onChange={(e) => {
                              const arr = [...(w.chartColors || CHART_COLORS)];
                              arr[i] = e.target.value;
                              updateWidget(w.id, { chartColors: arr });
                            }}
                            className="kpi-widget-config-color"
                            title={`Seria ${i + 1}`}
                          />
                        ))}
                      </div>
                    </label>
                  )}
                  <label>
                    Kolor tekstu:
                    <input
                      type="color"
                      value={w.textColor || '#f1f5f9'}
                      onChange={(e) => updateWidget(w.id, { textColor: e.target.value })}
                      className="kpi-widget-config-color"
                    />
                  </label>
                  <label>
                    Rozmiar czcionki tytułu:
                    <select
                      value={w.titleFontSize || 'default'}
                      onChange={(e) => updateWidget(w.id, { titleFontSize: e.target.value })}
                      className="kpi-widget-config-select"
                    >
                      <option value="small">Mały</option>
                      <option value="default">Domyślny</option>
                      <option value="large">Duży</option>
                    </select>
                  </label>
                </div>
              )}
              <div className="kpi-widget-card-body">{renderWidgetContent(w)}</div>
            </Card>
          </div>
        ))}
      </ResponsiveGridLayout>
      )}

      {widgets.length === 0 && (
        <div className="kpi-widget-empty-state">
          <Text>Brak widgetów. Kliknij „Dodaj widget”, aby dodać kafelek, wykres lub tabelę.</Text>
        </div>
      )}
    </div>
  );
}

export default KPIWidget;
